import {
  syncedGet,
  syncedSet,
  SERVER_URL,
  beginUiEdit,
  endUiEdit,
} from '../data/serverStore';
import React, { useState, useEffect, useMemo } from 'react';
import MoistureCalculator from './MoistureCalculator';
import WorkOrderResultsGrid from './WorkOrderResultsGrid';
import { db } from '../data/store';
import { getDefaultUnit, getParamMeta } from '../data/params';
import { getCategoryUnit, saveCategoryUnits } from '../data/categoryUnitDefaults';

// value = internal code stored on the result; label = what shows in the
// dropdown. Blank means "let the report auto-compute Pass/Fail/Less Than/
// Greater Than from the Acceptance Limits" — any other selection overrides that.
const VERDICT_OPTIONS = [
  { value:'',     label:'(auto)' },
  { value:'pass', label:'Pass' },
  { value:'fail', label:'Fail' },
  { value:'less', label:'Less Than' },
  { value:'more', label:'Greater Than' },
];

const BASE_UNITS = [
  'mg/L','ug/L','mg/kg','ug/kg','%','ppm','ppb',
  'BTU/lb','BTU/Gal','MJ/kg','API','cSt','cP',
  'NTU','SU','uS/cm','mS/cm','CFU/mL','gpd',
  '°F','°C','lb/ft3','g/mL','g/cm3',
  'meq/L','mmol/L','mg/m3','μg/m3','mL','L','mL/L',
];

const BASE_METHODS = [
  'D-93','D-97','D-130','D-240','D-287','D-445','D-473','D-482','D-524',
  'D-664','D-976','D-1500','D-1796','D-2274','D-2500','D-2896','D-4052',
  'D-4294','D-4737','D-5453','D-6079','D-6304',
  'D-3172','D-3173','D-3174','D-3175',
  'EPA 6010','EPA 6020','EPA 7470','EPA 7471',
  'EPA 150.1','EPA 160.1','EPA 160.2','EPA 300.0',
  'EPA 350.1','EPA 353.2','EPA 365.1','EPA 405.1',
  'EPA 410.4','EPA 415.1','EPA 9222','EPA 1664',
  'EPA Method 1311','SW-846','SM 2320B','SM 4500',
];

// Sample-prep digestion methods — separate from the analysis (ASTM/EPA)
// method above, since a metals result is typically reported against both:
// which digestion prepared the sample, and which method analyzed it.
const BASE_DIGESTION_METHODS = [
  'EPA 3005A','EPA 3010A','EPA 3015A','EPA 3020A',
  'EPA 3050B','EPA 3051A','EPA 3052',
  'SW-846 3005A','SW-846 3010A','SW-846 3050B',
  'N/A',
];

// Extraction/leaching methods are distinct from digestion methods. They are
// assigned only to the tests that use the resulting extract.
const BASE_EXTRACTION_METHODS = [
  'EPA 1311 (TCLP)','EPA 1312 (SPLP)',
  'EPA 3510C','EPA 3520C','EPA 3540C','EPA 3545A','EPA 3550C',
  'ASTM D95','N/A',
];

// ── Persistent prefs stored in localStorage ───────────────────────────────────
// sl_unit_prefs   : { [paramName]: lastUsedUnit }
// sl_method_prefs : { [paramName]: lastUsedMethod }
// sl_custom_units : [unit1, unit2, ...]   (user-added units)
// sl_custom_methods:[method1, ...]        (user-added methods)

const loadPrefs = (key, fallback) => {
  try { const v = (syncedGet(key, null) !== null ? JSON.stringify(syncedGet(key, null)) : null); return v ? JSON.parse(v) : fallback; }
  catch { return fallback; }
};
const savePrefs = (key, val) => {
  try { return syncedSet(key, val); }
  catch (error) { return Promise.reject(error); }
};

// Client-visible report comments use the same storage key as Reports.jsx.
// These are intentionally separate from internal laboratory notes.
const loadSampleReportComment = (sampleId) => {
  if (!sampleId) return '';
  const comments = loadPrefs('sl_sample_comments', {});
  return comments[sampleId] || '';
};
const saveSampleReportComment = (sampleId, text) => {
  if (!sampleId) return;
  const comments = loadPrefs('sl_sample_comments', {});
  comments[sampleId] = text;
  return savePrefs('sl_sample_comments', comments);
};

const unitPrefs    = loadPrefs('sl_unit_prefs',    {});
const methodPrefs  = loadPrefs('sl_method_prefs',  {});
const customUnits  = loadPrefs('sl_custom_units',  []);
const customMethods= loadPrefs('sl_custom_methods',[]);
const digestionMethodPrefs   = loadPrefs('sl_digestion_method_prefs',   {});
const customDigestionMethods = loadPrefs('sl_custom_digestion_methods', []);
const extractionMethodPrefs   = loadPrefs('sl_extraction_method_prefs',   {});
const customExtractionMethods = loadPrefs('sl_custom_extraction_methods', []);

// Merge base + custom, deduplicated
const getAllUnits   = () => [...new Set([...BASE_UNITS,   ...customUnits])];
const getAllMethods = () => [...new Set([...BASE_METHODS, ...customMethods])];
const getAllDigestionMethods = () => [...new Set([...BASE_DIGESTION_METHODS, ...customDigestionMethods])];
const getAllExtractionMethods = () => [...new Set([...BASE_EXTRACTION_METHODS, ...customExtractionMethods])];

// Save a new custom unit/method if not already in the base list
const rememberCustomUnit = (unit) => {
  if (!unit || BASE_UNITS.includes(unit)) return;
  const existing = loadPrefs('sl_custom_units', []);
  if (!existing.includes(unit)) {
    savePrefs('sl_custom_units', [...existing, unit]);
  }
};
const rememberCustomMethod = (method) => {
  if (!method || BASE_METHODS.includes(method)) return;
  const existing = loadPrefs('sl_custom_methods', []);
  if (!existing.includes(method)) {
    savePrefs('sl_custom_methods', [...existing, method]);
  }
};
const rememberCustomDigestionMethod = (method) => {
  if (!method || BASE_DIGESTION_METHODS.includes(method)) return;
  const existing = loadPrefs('sl_custom_digestion_methods', []);
  if (!existing.includes(method)) {
    savePrefs('sl_custom_digestion_methods', [...existing, method]);
  }
};
const rememberCustomExtractionMethod = (method) => {
  if (!method || BASE_EXTRACTION_METHODS.includes(method)) return;
  const existing = loadPrefs('sl_custom_extraction_methods', []);
  if (!existing.includes(method)) {
    savePrefs('sl_custom_extraction_methods', [...existing, method]);
  }
};

// Save last-used unit/method per parameter name
const matrixKey = matrix => matrix === 'S' ? 'S' : matrix === 'T' ? 'T' : 'L';
const matrixPrefKey = (base, matrix) => `${base}_${matrixKey(matrix)}`;

const saveUnitPref = (param, unit, matrix='L') => {
  const key = matrixPrefKey('sl_unit_prefs', matrix);
  const prefs = loadPrefs(key, {});
  prefs[param] = unit;
  // Return the server-backed write so callers that are saving a sample can
  // wait for the unit preference instead of announcing success while this
  // second write is still queued.
  const preferenceWrite = savePrefs(key, prefs);
  rememberCustomUnit(unit);
  return preferenceWrite;
};
const saveMethodPref = (param, method, matrix='L') => {
  const key = matrixPrefKey('sl_method_prefs', matrix);
  const prefs = loadPrefs(key, {});
  prefs[param] = method;
  const preferenceWrite = savePrefs(key, prefs);
  rememberCustomMethod(method);
  return preferenceWrite;
};
const saveDigestionMethodPref = (param, method) => {
  const prefs = loadPrefs('sl_digestion_method_prefs', {});
  prefs[param] = method;
  savePrefs('sl_digestion_method_prefs', prefs);
  rememberCustomDigestionMethod(method);
};
const saveExtractionMethodPref = (param, method) => {
  const prefs = loadPrefs('sl_extraction_method_prefs', {});
  prefs[param] = method;
  savePrefs('sl_extraction_method_prefs', prefs);
  rememberCustomExtractionMethod(method);
};

// Get the best default unit: last-used > ParamManager saved > param meta > system default
const getBestUnit = (param, matrix='L', category='Specific Analysis') => {
  const categoryDefaults = loadPrefs(
    `sl_category_unit_defaults_${String(category || 'Specific Analysis').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'specific_analysis'}_${matrixKey(matrix)}`,
    {}
  );
  if (Object.prototype.hasOwnProperty.call(categoryDefaults, param)) {
    return categoryDefaults[param];
  }
  if (matrixKey(matrix) === 'T') {
    const adminMeta = loadPrefs('sl_param_meta', {});
    return adminMeta[param]?.unitTclp ?? 'mg/L';
  }
  // Do not fall through to the old matrix-wide last-used maps here: that is
  // what allowed a Fuel choice to become a TCLP or Specific default.
  return getCategoryUnit(param, category, matrix);
};
const getBestSpec = (param, matrix='L') => {
  const resultDefaults = loadPrefs(matrixPrefKey('sl_result_field_defaults', matrix), {});
  if (Object.prototype.hasOwnProperty.call(resultDefaults[param] || {}, 'spec')) {
    return resultDefaults[param].spec;
  }
  const adminMeta = loadPrefs('sl_param_meta', {});
  const key = matrixKey(matrix);
  return key === 'S' ? (adminMeta[param]?.specSolid ?? adminMeta[param]?.spec ?? '')
    : key === 'T' ? (adminMeta[param]?.specTclp ?? '')
      : (adminMeta[param]?.specLiquid ?? adminMeta[param]?.spec ?? '');
};
const getBestMethod = (param, fallback, matrix='L') => {
  const resultDefaults = loadPrefs(matrixPrefKey('sl_result_field_defaults', matrix), {});
  if (resultDefaults[param]?.method) return resultDefaults[param].method;
  const prefs = loadPrefs(matrixPrefKey('sl_method_prefs', matrix), {});
  if (prefs[param]) return prefs[param];
  const adminMeta = loadPrefs('sl_param_meta', {});
  const key = matrixKey(matrix);
  const matrixMethod = key === 'S' ? adminMeta[param]?.methodSolid : key === 'T' ? adminMeta[param]?.methodTclp : adminMeta[param]?.methodLiquid;
  if (matrixMethod) return matrixMethod;
  if (key === 'L' && adminMeta[param]?.method) return adminMeta[param].method;
  return fallback || '';
};
const getBestDigestionMethod = (param, fallback) => {
  const prefs = loadPrefs('sl_digestion_method_prefs', {});
  if (prefs[param]) return prefs[param];
  const adminMeta = loadPrefs('sl_param_meta', {});
  if (adminMeta[param]?.digestionMethod) return adminMeta[param].digestionMethod;
  return fallback || '';
};
const getBestExtractionMethod = (param, fallback) => {
  const prefs = loadPrefs('sl_extraction_method_prefs', {});
  if (prefs[param]) return prefs[param];
  const adminMeta = loadPrefs('sl_param_meta', {});
  if (adminMeta[param]?.extractionMethod) return adminMeta[param].extractionMethod;
  return fallback || '';
};
const getBestReportingLimit = (param, fallback = '') => {
  const resultDefaults = loadPrefs('sl_result_field_defaults', {});
  if (resultDefaults[param]?.reportingLimit !== undefined &&
      String(resultDefaults[param].reportingLimit).trim() !== '') {
    return resultDefaults[param].reportingLimit;
  }
  const adminMeta = loadPrefs('sl_param_meta', {});
  if (adminMeta[param]?.reportingLimit !== undefined &&
      String(adminMeta[param].reportingLimit).trim() !== '') {
    return adminMeta[param].reportingLimit;
  }
  return fallback || '';
};

// Remember report fields by parameter for future samples. These are
// defaults only: every saved sample receives its own copy of the values, so
// changing a default later cannot rewrite an older report.
const saveResultFieldDefaults = async (params, savedResults, matrix='L', category='Specific Analysis') => {
  const key = matrixPrefKey('sl_result_field_defaults', matrix);
  const defaults = { ...loadPrefs(key, {}) };
  params.forEach(param => {
    const result = savedResults[param] || {};
    const previous = defaults[param] || {};
    const next = { ...previous };
    // Units are deliberately saved in the category-specific store below.
    const reportingLimit = result.reportingLimit ?? result.rdl;
    if (reportingLimit !== undefined && String(reportingLimit).trim() !== '') {
      next.reportingLimit = reportingLimit;
    }
    if (result.method !== undefined && String(result.method).trim() !== '') {
      next.method = result.method;
    }
    // Specification defaults are matrix-specific and may also be explicitly
    // cleared by the user.
    if (result.spec !== undefined) {
      next.spec = result.spec;
    }
    if (Object.keys(next).length) defaults[param] = next;
  });
  await Promise.all([
    savePrefs(key, defaults),
    matrixKey(matrix) === 'T' ? Promise.resolve() : saveCategoryUnits(category, matrix, params, savedResults),
  ]);
  return defaults;
};

// ── ComboCell — type freely + dropdown + remembers choices ───────────────────
// `kind` picks which pref store this cell reads/writes ('method' | 'unit' |
// 'digestion' | 'extraction'). Defaults from `mono` for backward compatibility with the
// two original call sites (mono was already being used as a de facto
// "is this a method field" flag) — `mono` itself stays purely about the
// monospace styling.
const COMBO_KIND_CONFIG = {
  method:    { customKey: 'sl_custom_methods',           save: saveMethodPref },
  unit:      { customKey: 'sl_custom_units',              save: saveUnitPref },
  digestion: { customKey: 'sl_custom_digestion_methods',  save: saveDigestionMethodPref },
  extraction:{ customKey: 'sl_custom_extraction_methods',  save: saveExtractionMethodPref },
};

function ComboCell({ value, onChange, onCommit, options, placeholder, mono, paramName, kind, matrix='L' }) {
  const [open,  setOpen]  = React.useState(false);
  const [query, setQuery] = React.useState('');
  const ref = React.useRef();
  const editedRef = React.useRef(false);

  const effectiveKind = kind || (mono ? 'method' : 'unit');
  const { customKey, save: savePref } = COMBO_KIND_CONFIG[effectiveKind];

  // Options: saved custom first, then base list, filtered by query
  const customSaved = loadPrefs(customKey, []);
  const allOpts = [...new Set([...customSaved, ...options])];
  const filtered = query
    ? allOpts.filter(o => o.toLowerCase().includes(query.toLowerCase()))
    : allOpts;

  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        // Commit on blur — save pref and remember custom value
        if (editedRef.current && paramName) {
          savePref(paramName, value, matrix);
          editedRef.current = false;
        }
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [value, paramName, effectiveKind]);

  const select = (opt) => {
    onChange(opt);
    setOpen(false);
    setQuery('');
    if (paramName) {
      savePref(paramName, opt, matrix);
    }
    editedRef.current = false;
    onCommit && onCommit(opt);
  };

  return (
    <div ref={ref} style={{position:'relative'}}>
      <input
        value={value || ''}
        onChange={e => { editedRef.current = true; onChange(e.target.value); setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setQuery(''); setOpen(true); }}
        onKeyDown={e => { if (e.key==='Enter') { select(value); e.target.blur(); } if (e.key==='Escape') setOpen(false); }}
        placeholder={placeholder || ''}
        style={{width:'100%', border:'1px solid #cdd3e4', borderRadius:3,
          padding:'2px 20px 2px 4px', fontSize:10, outline:'none',
          color:'#1e3060', textAlign:'center',
          fontFamily: mono ? 'monospace' : 'inherit',
          background: value ? 'white' : '#fafafa',
        }}
      />
      <span onClick={() => setOpen(v => !v)}
        style={{position:'absolute',right:3,top:'50%',transform:'translateY(-50%)',
          cursor:'pointer',fontSize:9,color:'#999',userSelect:'none'}}>▼</span>
      {open && (() => {
        const rect = ref.current?.getBoundingClientRect();
        return (
          <div style={{position:'fixed',top:(rect?.bottom||0)+2,left:rect?.left||0,
            width:Math.max(rect?.width||0, 160),
            background:'white',border:'1px solid #cdd3e4',borderRadius:3,
            boxShadow:'0 4px 16px rgba(0,0,0,0.18)',zIndex:99999,
            maxHeight:180,overflowY:'auto'}}>
          {/* "Add custom" hint when typing something new */}
          {query && !allOpts.includes(query) && (
            <div onMouseDown={e=>{e.preventDefault();select(query);}}
              style={{padding:'5px 8px',cursor:'pointer',fontSize:10,
                color:'#1a6b42',fontWeight:600,borderBottom:'1px solid #e8ecf8',
                background:'#f0fff8'}}>
              ＋ Add "{query}"
            </div>
          )}
          {filtered.length === 0 && !query && (
            <div style={{padding:'8px',color:'#aaa',fontSize:10,textAlign:'center'}}>
              Type to search or add new
            </div>
          )}
          {filtered.map(opt => (
            <div key={opt}
              onMouseDown={e => { e.preventDefault(); select(opt); }}
              style={{padding:'4px 8px',cursor:'pointer',fontSize:10,
                fontFamily: mono ? 'monospace' : 'inherit',
                color: customSaved.includes(opt) ? '#9c27b0' : '#1e3060',
                background: opt===value ? '#e8f0ff' : 'white',
                display:'flex',alignItems:'center',gap:6}}
              onMouseEnter={e => e.currentTarget.style.background='#f0f4ff'}
              onMouseLeave={e => e.currentTarget.style.background=opt===value?'#e8f0ff':'white'}>
              {customSaved.includes(opt) && (
                <span style={{fontSize:8,color:'#9c27b0',flexShrink:0}}>★</span>
              )}
              {opt}
            </div>
          ))}
        </div>
        );
      })()}
    </div>
  );
}

const todayStr = () => new Date().toISOString().split('T')[0];

// Parameters that should NOT be recalculated on dry/as-received basis
const SKIP_RECALC = new Set([
  'Moisture, %','Moisture,%','Moisture-Air Dried, %','Moisture','moisture',
  'pH','Appearance','Color','Odor','Turbidity','Conductivity','Specific Gravity',
  'Specific Conductance','Flash Point, °F','Flash Point','Pour Point, °F',
  'Cloud Point, °F','API Gravity @ 60°F','Density',
]);

const isMoistureParam = (p) =>
  p.toLowerCase().includes('moisture') && p.toLowerCase().includes('%');
const isAshParam = (p) =>
  (p.toLowerCase().startsWith('ash') || p === 'Ash,%' || p === "Ash,% - As Rec'd Basis") &&
  p.toLowerCase().includes('%');

// Moisture/basis choices belong to one sample. Keep a direct sample-level
// record instead of trying to infer the UI state only from calculated result
// rows. Legacy samples still restore from recalcParams/moisturePercent.
const getSavedMoistureBasis = sample => {
  const saved = sample?.moistureBasis && typeof sample.moistureBasis === 'object'
    ? sample.moistureBasis
    : {};
  const savedResults = sample?.results || {};
  const withMoisture = Object.values(savedResults).find(result => result?.moisturePercent != null);
  const legacyRecalc = Array.isArray(sample?.recalcParams) ? sample.recalcParams : [];
  return {
    enabled: saved.enabled !== undefined ? Boolean(saved.enabled) : Boolean(withMoisture),
    moisturePercent: saved.moisturePercent !== undefined && saved.moisturePercent !== null
      ? String(saved.moisturePercent)
      : (withMoisture ? String(withMoisture.moisturePercent) : ''),
    recalcParams: Array.isArray(saved.recalcParams) ? saved.recalcParams : legacyRecalc,
    reportMode: ['dry','wet','both'].includes(saved.reportMode) ? saved.reportMode : 'both',
    showBasis: saved.showBasis !== undefined ? Boolean(saved.showBasis) : true,
  };
};

// ── Basis calculations ────────────────────────────────────────────────────────
// Input is DRY BASIS (measured value). Back-calculate As Received basis.
//   As Received (AR) = DB × (1 − M%/100)

function calcWetBasis(dbVal, moisturePct) {
  const db = parseFloat(dbVal);
  const m  = parseFloat(moisturePct);
  if (isNaN(db) || isNaN(m) || m >= 100) return null;
  return db * (1 - m / 100);
}


// Store calculated AR values at high precision, then format them for display
// with the same decimal places the technician entered for Dry Basis. This
// keeps BTU values as whole numbers while preserving values such as 0.0128.
function storeCalculatedValue(n) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '';
  return Number(n).toPrecision(15).replace(/(?:\.0+|(\.\d+?)0+)$/, '$1');
}

function round4(n) {
  if (n === null || n === undefined) return '';
  return parseFloat(n.toFixed(4)).toString();
}

function enteredDecimalPlaces(value) {
  const text = String(value ?? '').trim().replace(/,/g, '');
  const match = text.match(/^[+-]?(?:\d+(?:\.(\d*))?|\.([0-9]+))(?:e([+-]?\d+))?$/i);
  if (!match) return 4;
  const fractionLength = (match[1] || match[2] || '').length;
  const exponent = Number(match[3] || 0);
  return Math.max(0, Math.min(10, fractionLength - exponent));
}

function formatAsReceived(n, dryBasisValue) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '';
  return Number(n).toFixed(enteredDecimalPlaces(dryBasisValue));
}

// ── MoistureBasisPanel ────────────────────────────────────────────────────────
function MoistureBasisPanel({ params, results, onToggleRecalc, recalcParams, moistureVal, ashVal }) {
  const hasMoisture = moistureVal !== '' && !isNaN(parseFloat(moistureVal));
  const hasAsh      = ashVal      !== '' && !isNaN(parseFloat(ashVal));
  const m = parseFloat(moistureVal);
  const a = parseFloat(ashVal);

  const recalcable = params.filter(p =>
    !isMoistureParam(p) && !isAshParam(p) && !SKIP_RECALC.has(p)
  );

  return (
    <div style={{background:'#f0f8ff',border:'1px solid #b8d4f0',borderRadius:6,
      padding:'10px 14px',marginBottom:10}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
        <span style={{fontSize:13,fontWeight:700,color:'#1e3060'}}>💧 Moisture Basis Calculator</span>
        <span style={{fontSize:11,color:'#888'}}>
          {hasMoisture ? `Moisture: ${m}%` : '— enter Moisture % below'}
          {hasAsh ? ` | Ash: ${a}%` : ''}
        </span>
      </div>

      {!hasMoisture && (
        <div style={{fontSize:11,color:'#e09900',fontWeight:500,marginBottom:6}}>
          ⚠️ Enter a Moisture % value in the results below to enable calculations.
        </div>
      )}

      {hasMoisture && (
        <>
          <div style={{fontSize:11,color:'#1e3060',marginBottom:8,fontWeight:500}}>
            Select which parameters to report as As Received (back-calculated from Dry Basis):
          </div>
          <div style={{display:'flex',gap:6,marginBottom:8}}>
            <button
              className="btn btn-sm"
              onClick={()=>recalcable.forEach(p=>onToggleRecalc(p,true))}>
              ☑ Select All
            </button>
            <button
              className="btn btn-sm"
              onClick={()=>recalcable.forEach(p=>onToggleRecalc(p,false))}>
              ☐ Clear All
            </button>
            <span style={{fontSize:11,color:'#888',alignSelf:'center'}}>
              {recalcParams.size} selected
            </span>
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:'3px 8px'}}>
            {recalcable.map(p => (
              <label key={p} style={{display:'flex',alignItems:'center',gap:4,
                fontSize:11,cursor:'pointer',padding:'2px 6px',borderRadius:3,
                background: recalcParams.has(p) ? '#1e3060' : '#e8ecf8',
                color: recalcParams.has(p) ? 'white' : '#555',
                transition:'all 0.1s'}}>
                <input type="checkbox" checked={recalcParams.has(p)}
                  onChange={e=>onToggleRecalc(p,e.target.checked)}
                  style={{accentColor:'#1e3060',cursor:'pointer',width:13,height:13}}/>
                {p}
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── ResultsTable ─────────────────────────────────────────────────────────────
// reportMode: 'dry' | 'wet' | 'both'
// ── Results Sheet attachments (per sample) ──────────────────────────────────
// Lets an on-site tech scan/upload a photo of their handwritten raw-results
// worksheet, so someone working remotely can view/download it later from
// Log Report without needing the physical paper handed to them.
const loadResultsSheetMap = () => {
  try { const v = syncedGet('results_sheets_by_sample', null); return v !== null ? JSON.parse(JSON.stringify(v)) : {}; }
  catch { return {}; }
};
const saveResultsSheetMap = (map) => { try { syncedSet('results_sheets_by_sample', map); } catch {} };
const getResultsSheetIds = (sampleId) => loadResultsSheetMap()[sampleId] || [];
const addResultsSheetId = (sampleId, sheetId) => {
  const map = loadResultsSheetMap();
  const existing = map[sampleId] || [];
  if (!existing.includes(sheetId)) saveResultsSheetMap({ ...map, [sampleId]: [...existing, sheetId] });
};
const removeResultsSheetId = (sampleId, sheetId) => {
  const map = loadResultsSheetMap();
  saveResultsSheetMap({ ...map, [sampleId]: (map[sampleId]||[]).filter(id => id !== sheetId) });
};

function ResultsSheetPanel({ sampleId, toast }) {
  const [sheetIds, setSheetIds] = useState(() => getResultsSheetIds(sampleId));
  const [scanning, setScanning] = useState(false);
  const [viewer, setViewer] = useState(null); // sheetId currently open in the viewer
  const [rotation, setRotation] = useState(90);
  const [zoom, setZoom] = useState(0.5);
  const fileInputRef = React.useRef();
  const resetView = () => { setRotation(90); setZoom(0.5); };

  React.useEffect(() => { setSheetIds(getResultsSheetIds(sampleId)); }, [sampleId]);

  const handleScan = async () => {
    setScanning(true);
    try {
      const listRes = await fetch(`${SERVER_URL}/list-scanners`);
      const { scanners } = await listRes.json();
      const deviceIndex = scanners?.[0]?.idx || 1;
      const res = await fetch(`${SERVER_URL}/scan-results-sheet`, {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ deviceIndex }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Scan failed');
      addResultsSheetId(sampleId, data.sheetId);
      setSheetIds(getResultsSheetIds(sampleId));
      toast('success', 'Results sheet scanned and attached');
    } catch(e) {
      toast('error', e.message);
    } finally {
      setScanning(false);
    }
  };

  const handleUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const res = await fetch(`${SERVER_URL}/upload-results-sheet`, {
          method: 'POST', headers: {'Content-Type':'application/json'},
          body: JSON.stringify({ image: reader.result }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'Upload failed');
        addResultsSheetId(sampleId, data.sheetId);
        setSheetIds(getResultsSheetIds(sampleId));
        toast('success', 'Results sheet uploaded and attached');
      } catch(e) {
        toast('error', e.message);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemove = (sheetId, e) => {
    e.stopPropagation();
    removeResultsSheetId(sampleId, sheetId);
    setSheetIds(getResultsSheetIds(sampleId));
  };

  const handlePrint = (url, rotationDeg) => {
    const isRotated = rotationDeg === 90 || rotationDeg === 270;
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>Print Results Sheet</title>
      <style>
        @page { size: ${isRotated ? 'landscape' : 'portrait'}; margin: 0.25in; }
        body{margin:0;display:flex;align-items:center;justify-content:center;min-height:100vh;background:white;}
        img{
          ${isRotated ? 'max-width:95vh;max-height:95vw;' : 'max-width:95vw;max-height:95vh;'}
          transform:rotate(${rotationDeg}deg);
        }
        @media print{ body{min-height:auto;} }
      </style></head><body>
      <img src="${url}" onload="window.print();"/>
      </body></html>`);
    win.document.close();
  };

  if (!sampleId) return null;

  return (
    <div style={{border:'1px solid #e0d8f0',borderRadius:6,padding:8,marginBottom:8,background:'#faf7ff'}}>
      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
        <span style={{fontSize:11,fontWeight:700,color:'#7c3aed'}}>📄 Results Sheet(s)</span>
        <button onClick={handleScan} disabled={scanning}
          style={{fontSize:10,padding:'3px 8px',borderRadius:3,cursor:scanning?'wait':'pointer',
            border:'1px solid #7c3aed',background:'white',color:'#7c3aed'}}>
          {scanning ? '⏳ Scanning...' : '🖨 Scan'}
        </button>
        <button onClick={()=>fileInputRef.current?.click()}
          style={{fontSize:10,padding:'3px 8px',borderRadius:3,cursor:'pointer',
            border:'1px solid #7c3aed',background:'white',color:'#7c3aed'}}>
          📁 Upload
        </button>
        <input ref={fileInputRef} type="file" accept="image/*,.pdf" style={{display:'none'}}
          onChange={e=>{ handleUpload(e.target.files[0]); e.target.value=''; }}/>
        {sheetIds.map(id=>(
          <div key={id} style={{position:'relative'}}>
            <img src={`${SERVER_URL}/results-sheet/${id}`} alt=""
              onClick={()=>{setViewer(id);resetView();}}
              style={{width:34,height:34,objectFit:'cover',borderRadius:3,cursor:'pointer',border:'1px solid #cdd3e4'}}/>
            <button onClick={e=>handleRemove(id,e)}
              title="Remove"
              style={{position:'absolute',top:-5,right:-5,width:14,height:14,borderRadius:'50%',
                border:'none',background:'#dc2626',color:'white',fontSize:9,cursor:'pointer',lineHeight:'14px',padding:0}}>
              ✕
            </button>
          </div>
        ))}
      </div>
      {viewer && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.8)',zIndex:4000,
          display:'flex',alignItems:'center',justifyContent:'center'}}
          onClick={()=>setViewer(null)}>
          <div style={{background:'white',borderRadius:8,padding:12,width:'98vw',height:'98vh',
            display:'flex',flexDirection:'column'}}
            onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'flex-end',gap:6,marginBottom:8,flexShrink:0,alignItems:'center'}}>
              <button onClick={()=>setZoom(z=>Math.max(0.5,z-0.25))} style={{fontSize:11,padding:'3px 8px',cursor:'pointer'}}>−</button>
              <span style={{fontSize:11,color:'#555',minWidth:34,textAlign:'center'}}>{Math.round(zoom*100)}%</span>
              <button onClick={()=>setZoom(z=>Math.min(4,z+0.25))} style={{fontSize:11,padding:'3px 8px',cursor:'pointer'}}>+</button>
              <button onClick={()=>setRotation(r=>(r-90+360)%360)} style={{fontSize:11,padding:'3px 8px',cursor:'pointer'}}>↺</button>
              <button onClick={()=>setRotation(r=>(r+90)%360)} style={{fontSize:11,padding:'3px 8px',cursor:'pointer'}}>↻</button>
              <button onClick={()=>handlePrint(`${SERVER_URL}/results-sheet/${viewer}`, rotation)}
                style={{fontSize:11,padding:'3px 8px',cursor:'pointer',
                  background:'#0078d4',color:'white',border:'none',borderRadius:3}}>
                🖨 Print
              </button>
              <a href={`${SERVER_URL}/results-sheet/${viewer}`} download={`ResultsSheet_${sampleId}.jpg`}
                style={{fontSize:11,padding:'3px 8px',cursor:'pointer',textDecoration:'none',
                  background:'#16a34a',color:'white',borderRadius:3}}>
                ⬇ Download
              </a>
              <button onClick={()=>setViewer(null)} style={{fontSize:14,padding:'3px 8px',cursor:'pointer'}}>✕</button>
            </div>
            <div style={{flex:1,overflow:'auto',textAlign:'center',background:'#f4f5f7',borderRadius:6}}>
              <img src={`${SERVER_URL}/results-sheet/${viewer}`} alt=""
                style={{minWidth: zoom>1 ? `${zoom*100}%` : undefined,
                  width: zoom<=1 ? `${zoom*100}%` : undefined,
                  transform:`rotate(${rotation}deg)`,
                  transformOrigin:'center center', transition:'transform 0.3s',
                  margin:'20px auto', display:'block'}}/>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MethodAssignmentBlock({
  params, results, label, field, color, value, setValue, options, selected,
  setSelected, kind, remember, onToggle, onApply, onSetNotApplicable
}) {
  return (
    <div style={{border:`1px solid ${color}55`,borderRadius:6,padding:9,background:'#fff'}}>
      <div style={{display:'grid',gridTemplateColumns:'150px minmax(190px,1fr) auto auto',
        gap:6,alignItems:'center'}}>
        <strong style={{fontSize:11,color}}>{label}</strong>
        <ComboCell value={value} onChange={setValue} options={options}
          placeholder={`Select or enter ${label.toLowerCase()}`} mono kind={kind}/>
        <button type="button" className="btn btn-sm"
          onClick={()=>setSelected([...params])}>Select All</button>
        <button type="button" className="btn btn-sm"
          onClick={()=>setSelected([])}>Clear Checks</button>
      </div>
      <div style={{display:'flex',flexWrap:'wrap',gap:'5px 12px',marginTop:8,
        padding:'7px 8px',background:'#f8f9fc',borderRadius:4,maxHeight:100,overflowY:'auto'}}>
        {params.map(p => (
          <label key={`${field}-${p}`} style={{display:'flex',alignItems:'center',gap:4,
            fontSize:10,cursor:'pointer',minWidth:150}}>
            <input type="checkbox" checked={selected.includes(p)}
              onChange={()=>onToggle(setSelected, selected, p)}
              style={{accentColor:color}}/>
            <span>{p}</span>
            {results[p]?.[`${field}Assigned`] === true && results[p]?.[field] && (
              <span title={results[p][field]} style={{color:'#777',fontSize:9}}>
                [{results[p][field]}]
              </span>
            )}
          </label>
        ))}
      </div>
      <div style={{display:'flex',gap:6,marginTop:7}}>
        <button type="button" className="btn btn-sm"
          style={{background:color,borderColor:color,color:'white'}}
          onClick={()=>onApply(label, field, value, selected, remember)}>
          Apply to Checked Tests
        </button>
        <button type="button" className="btn btn-sm"
          onClick={()=>onSetNotApplicable(label, field, selected)}>
          Set Checked Tests to N/A
        </button>
      </div>
    </div>
  );
}

function MethodAssignmentPanel({ params, results, setResult }) {
  const savedMethodFor = (field) => {
    const assignedMethods = [...new Set(
      params
        .filter(p => results[p]?.[`${field}Assigned`] === true)
        .map(p => String(results[p]?.[field] || '').trim())
        .filter(Boolean)
    )];
    return assignedMethods.length === 1 ? assignedMethods[0] : '';
  };
  // When every assigned test uses the same saved method, show it in the
  // selector. Previously the selector reopened blank even though each row
  // displayed a saved method, so clicking Apply after unchecking Mercury did
  // nothing except ask the technician to select a method again.
  const [extractionMethod, setExtractionMethod] = useState(
    () => savedMethodFor('extractionMethod')
  );
  const [digestionMethod, setDigestionMethod] = useState(
    () => savedMethodFor('digestionMethod')
  );
  const [extractionTests, setExtractionTests] = useState(
    () => params.filter(p => results[p]?.extractionMethodAssigned === true)
  );
  const [digestionTests, setDigestionTests] = useState(
    () => params.filter(p => results[p]?.digestionMethodAssigned === true)
  );
  const [message, setMessage] = useState('');

  const toggleTest = (setter, current, param) => {
    setter(current.includes(param)
      ? current.filter(p => p !== param)
      : [...current, param]);
  };

  const applyMethod = (label, field, method, selected, remember) => {
    const normalizedMethod = String(method).trim();
    const allUncheckedAssignments = params.filter(p =>
      !selected.includes(p) &&
      results[p]?.[`${field}Assigned`] === true
    );

    // A blank selector must not prevent a removal. The checkbox list itself
    // is an explicit instruction: an assigned test that was unchecked should
    // be cleared and saved. This is the path used when removing Mercury from
    // an existing digestion/extraction group.
    if (!normalizedMethod) {
      if (!allUncheckedAssignments.length) {
        setMessage(`Select a ${label.toLowerCase()} first.`);
        return;
      }
      allUncheckedAssignments.forEach(p => {
        setResult(p, field, '');
        setResult(p, `${field}Assigned`, false);
      });
      setMessage(
        `${label} removed from ${allUncheckedAssignments.length} unchecked test` +
        `${allUncheckedAssignments.length === 1 ? '' : 's'}. ` +
        `Click Save Results to update the printed report.`
      );
      return;
    }
    const removed = params.filter(p =>
      !selected.includes(p) &&
      results[p]?.[`${field}Assigned`] === true &&
      String(results[p]?.[field] || '').trim() === normalizedMethod
    );
    selected.forEach(p => {
      setResult(p, field, method);
      setResult(p, `${field}Assigned`, true);
    });
    removed.forEach(p => {
      setResult(p, field, '');
      setResult(p, `${field}Assigned`, false);
    });
    remember(method);
    const removedText = removed.length
      ? ` Removed from ${removed.length} unchecked test${removed.length === 1 ? '' : 's'}.`
      : '';
    setMessage(
      `${label} applied to ${selected.length} checked test${selected.length === 1 ? '' : 's'}.` +
      `${removedText} Click Save Results to update the printed report.`
    );
  };

  const setNotApplicable = (label, field, selected) => {
    const removed = params.filter(p =>
      !selected.includes(p) &&
      results[p]?.[`${field}Assigned`] === true &&
      results[p]?.[field] === 'N/A'
    );
    selected.forEach(p => {
      setResult(p, field, 'N/A');
      setResult(p, `${field}Assigned`, true);
    });
    removed.forEach(p => {
      setResult(p, field, '');
      setResult(p, `${field}Assigned`, false);
    });
    const removedText = removed.length
      ? ` Removed N/A from ${removed.length} unchecked test${removed.length === 1 ? '' : 's'}.`
      : '';
    setMessage(
      `${label} set to N/A for ${selected.length} checked test${selected.length === 1 ? '' : 's'}.` +
      `${removedText} Click Save Results to update the printed report.`
    );
  };

  return (
    <details style={{marginBottom:8,border:'1px solid #ccd5e5',borderRadius:6,
      background:'#f4f7fc'}}>
      <summary style={{padding:'8px 10px',cursor:'pointer',fontSize:11,
        fontWeight:700,color:'#1e3060'}}>
        🧪 Assign Digestion and Extraction Methods to Selected Tests
      </summary>
      <div style={{padding:'0 9px 9px',display:'grid',gap:8}}>
        <MethodAssignmentBlock params={params} results={results}
          label="Digestion Method" field="digestionMethod"
          color="#6a3d8f" value={digestionMethod} setValue={setDigestionMethod}
          options={getAllDigestionMethods()} selected={digestionTests}
          setSelected={setDigestionTests} kind="digestion"
          remember={rememberCustomDigestionMethod} onToggle={toggleTest}
          onApply={applyMethod} onSetNotApplicable={setNotApplicable}/>
        <MethodAssignmentBlock params={params} results={results}
          label="Extraction Method" field="extractionMethod"
          color="#1976a3" value={extractionMethod} setValue={setExtractionMethod}
          options={getAllExtractionMethods()} selected={extractionTests}
          setSelected={setExtractionTests} kind="extraction"
          remember={rememberCustomExtractionMethod} onToggle={toggleTest}
          onApply={applyMethod} onSetNotApplicable={setNotApplicable}/>
        {message && <div style={{fontSize:10,color:'#1a6b42',fontWeight:600}}>{message}</div>}
        <div style={{fontSize:9,color:'#777'}}>
          Methods are optional. A method is saved and printed only after it is applied to a checked test.
          Use N/A when the preparation step explicitly does not apply.
        </div>
      </div>
    </details>
  );
}

function ResultsTable({ sampleId, params, results, setResult, recalcParams, matrix, category,
  moistureVal, ashVal, showBasis, reportMode, showWetCol, quickMoisture, showQAQC }) {

  const [selectedCell, setSelectedCell] = useState(null);
  const [copyMessage, setCopyMessage] = useState('');

  useEffect(() => {
    setSelectedCell(null);
    setCopyMessage('');
  }, [sampleId]);

  const m   = parseFloat(moistureVal);
  const ash = parseFloat(ashVal);
  const hasMoisture = !isNaN(m) && m < 100;
  const hasAsh      = !isNaN(ash) && ash < 100;

  const showWet  = showBasis && hasMoisture && (reportMode === 'wet'  || reportMode === 'both');
  const showDB   = reportMode === 'dry' || reportMode === 'both' || !showBasis;
  // The complete results grid is wider than a normal content panel. Giving
  // every column a real pixel width prevents browsers from compressing inputs
  // into one another; the existing wrapper then provides horizontal scrolling.
  const tableMinWidth = 1100 + (showWet ? 100 : 0) + (showWetCol ? 110 : 0);

  const selectCell = (param, column) => {
    setSelectedCell({ param, column });
    setCopyMessage('');
  };

  const columnCellStyle = (param, column, base={}) => selectedCell?.param === param &&
    selectedCell?.column === column
      ? { ...base, background:'#fff4c7', boxShadow:'inset 0 0 0 2px #e0a000' }
      : base;

  const getColumnValue = (param, column) => {
    const r = results[param] || {};
    switch (column) {
      case 'verdict': return r.verdict || '';
      case 'value': return r.value || '';
      case 'unit': return Object.prototype.hasOwnProperty.call(r, 'unit')
        ? r.unit
        : (getBestUnit(param, matrix, category) ?? getDefaultUnit(param));
      case 'reportingLimit':
        return r.reportingLimit !== undefined
          ? r.reportingLimit
          : (r.rdl !== undefined ? r.rdl : getBestReportingLimit(param));
      case 'method':
        return r.method !== undefined ? r.method : getBestMethod(param, getParamMeta(param).method, matrix);
      case 'dilutionFactor':
        return r.dilutionFactor !== undefined ? r.dilutionFactor : '';
      case 'spec':
        return r.spec !== undefined ? r.spec : getBestSpec(param, matrix);
      case 'dateTime':
        return { dateTested:r.dateTested || todayStr(), timeTested:r.timeTested || '' };
      default: return '';
    }
  };

  const copySelectedColumn = (column, label) => {
    if (!selectedCell || selectedCell.column !== column) {
      setCopyMessage(`Select a cell in the ${label} column first, then click its ↓ arrow.`);
      return;
    }
    const sourceValue = getColumnValue(selectedCell.param, column);
    if (column === 'dateTime') {
      params.forEach(p => {
        setResult(p, 'dateTested', sourceValue.dateTested);
        setResult(p, 'timeTested', sourceValue.timeTested);
      });
    } else {
      params.forEach(p => setResult(p, column, sourceValue));
    }
    const shownValue = column === 'dateTime'
      ? [sourceValue.dateTested, sourceValue.timeTested].filter(Boolean).join(' ')
      : (sourceValue === '' ? '(blank)' : String(sourceValue));
    setCopyMessage(
      `Copied ${label} “${shownValue}” from ${selectedCell.param} to all ${params.length} rows.`
    );
  };

  const CopyArrow = ({ column, label }) => (
    <button type="button" className="copy-column-button"
      title={`Copy the selected ${label} cell to every row`}
      onClick={e => { e.preventDefault(); e.stopPropagation(); copySelectedColumn(column, label); }}>
      ↓
    </button>
  );

  return (
    <>
    <MethodAssignmentPanel key={sampleId} params={params} results={results} setResult={setResult}/>
    {copyMessage && (
      <div style={{margin:'0 0 6px',padding:'5px 9px',borderRadius:4,fontSize:10,
        fontWeight:600,color:copyMessage.startsWith('Select')?'#8a5a00':'#17643a',
        background:copyMessage.startsWith('Select')?'#fff7df':'#eaf8f0',
        border:`1px solid ${copyMessage.startsWith('Select')?'#e3c66a':'#9fd0b3'}`}}>
        {copyMessage}
      </div>
    )}
    <div style={{overflowX:'auto',overflowY:'visible'}}>
      <style>{`
        .results-entry-table,
        .results-entry-table * {
          box-sizing: border-box;
        }
        .results-entry-table th {
          white-space: normal;
          overflow-wrap: anywhere;
          vertical-align: middle;
        }
        .copy-column-button {
          margin-left: 5px;
          width: 18px;
          height: 18px;
          padding: 0;
          border: 1px solid rgba(255,255,255,.75);
          border-radius: 3px;
          background: rgba(255,255,255,.16);
          color: white;
          font-size: 13px;
          font-weight: 800;
          line-height: 15px;
          cursor: pointer;
          vertical-align: middle;
        }
        .copy-column-button:hover {
          background: white;
          color: #1e3060;
        }
      `}</style>
      <table className="results-entry-table" style={{width:'100%',minWidth:tableMinWidth,
        tableLayout:'fixed',borderCollapse:'collapse',fontSize:11,position:'relative'}}>
        <colgroup>
          <col style={{width:220}}/>
          <col style={{width:95}}/>
          <col style={{width:110}}/>
          {showWet && <col style={{width:100}}/>}
          {showWetCol && <col style={{width:110}}/>}
          <col style={{width:85}}/>
          <col style={{width:80}}/>
          <col style={{width:120}}/>
          <col style={{width:70}}/>
          <col style={{width:150}}/>
          <col style={{width:170}}/>
        </colgroup>
        <thead>
          <tr style={{background:'#1e3060',color:'white'}}>
            <th style={{padding:'6px 8px',textAlign:'left'}}>Parameter</th>
            <th style={{padding:'6px 8px',textAlign:'center',background:'#7a1a3a'}}>
              Verdict <CopyArrow column="verdict" label="Verdict"/>
            </th>
            <th style={{padding:'6px 8px',textAlign:'center',background:'#2a5080',
              borderLeft:'2px solid #7ec8e388'}}>
              {(showWet || showWetCol) ? 'Dry Basis' : 'Result'}
              <CopyArrow column="value" label={(showWet || showWetCol) ? 'Dry Basis' : 'Result'}/><br/>
              <span style={{fontSize:9,fontWeight:400,opacity:0.8}}>← enter value</span>
            </th>
            {showWet && (
              <th style={{padding:'6px 8px',textAlign:'center',background:'#1a7dc4'}}>
                As Received (AR)<br/>
                <span style={{fontSize:9,fontWeight:400,opacity:0.8}}>(Recalc)</span>
              </th>
            )}
            {showWetCol && (
              <th style={{padding:'6px 8px',textAlign:'center',background:'#1565a0',
                borderLeft:'2px solid #4499cc'}}>
                As Received (AR)<br/>
                <span style={{fontSize:9,fontWeight:400,opacity:0.8}}>
                  {quickMoisture ? `M=${quickMoisture}%` : 'enter M% ↑'}
                </span>
              </th>
            )}
            <th style={{padding:'6px 8px',textAlign:'center'}}>
              Unit <CopyArrow column="unit" label="Unit"/>
            </th>
            <th style={{padding:'6px 8px',textAlign:'center',background:'#3a5070'}}>
              RL <CopyArrow column="reportingLimit" label="RL"/>
            </th>
            <th style={{padding:'6px 8px',textAlign:'center',background:'#1a4060'}}>
              Analytical Method <CopyArrow column="method" label="Analytical Method"/>
            </th>
            <th style={{padding:'6px 8px',textAlign:'center',background:'#345f7a'}}>
              DF <CopyArrow column="dilutionFactor" label="DF"/>
            </th>
            <th style={{padding:'6px 8px',textAlign:'center',background:'#1a3050'}}>
              Specification /<br/>Acceptance Limits
              <CopyArrow column="spec" label="Acceptance Limits"/>
            </th>
            <th style={{padding:'6px 8px',textAlign:'center'}}>
              Date Tested <CopyArrow column="dateTime" label="Date/Time Tested"/>
            </th>
          </tr>
        </thead>
        <tbody>
          {params.map((p, i) => {
            const r = results[p] || {
              value:'', unit:getDefaultUnit(p), qual:'', dateTested:todayStr()
            };
            const doRecalc  = recalcParams.has(p) && hasMoisture;
            const wet_val   = doRecalc ? calcWetBasis(r.value, m)   : null;
            // Quick wet basis from toolbar moisture input
            const qm = parseFloat(quickMoisture);
            const quickWet = showWetCol && recalcParams.has(p) &&
              !isNaN(qm) && qm < 100 && r.value
              ? calcWetBasis(r.value, qm) : null;

            const rowBg     = i % 2 === 0 ? 'white' : '#fafbff';
            const isMoist   = isMoistureParam(p);
            const isAsh2    = isAshParam(p);

            return (
              <tr key={p} style={{background: isMoist||isAsh2 ? '#fffbee' : rowBg,
                borderBottom:'1px solid #eef0f8'}}>

                {/* Parameter name */}
                <td style={{padding:'4px 8px',
                  fontWeight:(isMoist||isAsh2)?700:400,
                  color:isMoist?'#1a7dc4':isAsh2?'#9c27b0':'#333'}}>
                  {p}
                  {isMoist && <span style={{fontSize:9,marginLeft:4,background:'#1a7dc4',
                    color:'white',borderRadius:8,padding:'1px 5px'}}>MOISTURE</span>}
                  {isAsh2 && <span style={{fontSize:9,marginLeft:4,background:'#9c27b0',
                    color:'white',borderRadius:8,padding:'1px 5px'}}>ASH</span>}
                </td>

                {/* Verdict — first result field after the parameter name */}
                <td onFocusCapture={()=>selectCell(p,'verdict')}
                  style={columnCellStyle(p,'verdict',{padding:'2px 3px'})}>
                  <select value={r.verdict || ''}
                    onChange={e=>setResult(p,'verdict',e.target.value)}
                    style={{width:'100%',border:'1px solid #c8a8b8',borderRadius:3,
                      padding:'2px 3px',fontSize:10,outline:'none',
                      color: r.verdict==='fail'||r.verdict==='less'||r.verdict==='more' ? '#b83232'
                        : r.verdict==='pass' ? '#1a6b42' : '#888',
                      fontWeight: r.verdict ? 700 : 400,
                      background: r.verdict ? '#fdf5f7' : 'white'}}>
                    {VERDICT_OPTIONS.map(v=>(
                      <option key={v.value} value={v.value}>{v.label}</option>
                    ))}
                  </select>
                </td>

                {/* Result input — always shown */}
                <td onFocusCapture={()=>selectCell(p,'value')}
                  style={columnCellStyle(p,'value',{padding:'3px 4px',borderLeft:'2px solid #7ec8e388'})}>
                  <input
                    value={r.value}
                    onChange={e=>setResult(p,'value',e.target.value)}
                    placeholder="enter result"
                    style={{width:'100%',border:'1px solid #aac8ee',borderRadius:3,
                      padding:'3px 8px',fontSize:12,textAlign:'right',outline:'none',
                      background: isMoist||isAsh2 ? '#fffbee' : 'white',
                      fontWeight:600,
                      borderColor: r.value&&r.value!=='' ? '#1a7dc4' : '#cdd3e4'}}/>
                </td>

                {/* Wet Basis — selected per parameter and displayed immediately after Result */}
                {showWet && (
                  <td style={{padding:'3px 6px',textAlign:'right',
                    background:'#e8f4ff',fontWeight:600,fontSize:11,
                    color: doRecalc && wet_val!==null ? '#1a7dc4' : '#ccc'}}>
                    {isMoist||isAsh2 ? (
                      <span style={{color:'#888',fontStyle:'italic',fontWeight:400}}>
                        {r.value||'—'}
                      </span>
                    ) : (
                      doRecalc && wet_val!==null ? formatAsReceived(wet_val, r.value) : (doRecalc?'—':'')
                    )}
                  </td>
                )}

                {/* Quick Wet Basis — selected per parameter and displayed immediately after Result */}
                {showWetCol && (
                  <td style={{padding:'3px 6px',textAlign:'right',
                    background:'#dceeff',fontWeight:700,fontSize:12,
                    borderLeft:'2px solid #1565a0',
                    color: quickWet!==null ? '#1565a0' : '#ccc',
                    minWidth:90}}>
                    {isMoist ? (
                      <span style={{color:'#888',fontSize:10,fontWeight:400}}>
                        (M param)
                      </span>
                    ) : recalcParams.has(p) && quickWet !== null ? formatAsReceived(quickWet, r.value) : (
                      recalcParams.has(p) && r.value
                        ? <span style={{fontSize:10,color:'#ccc'}}>enter M%</span>
                        : ''
                    )}
                  </td>
                )}

                {/* Unit — combo dropdown with memory */}
                <td onFocusCapture={()=>selectCell(p,'unit')}
                  style={columnCellStyle(p,'unit',{padding:'2px 3px'})}>
                  <ComboCell
                    value={Object.prototype.hasOwnProperty.call(r, 'unit')
                      ? r.unit
                      : (getBestUnit(p, matrix, category) ?? getDefaultUnit(p))}
                    onChange={v=>setResult(p,'unit',v)}
                    options={getAllUnits()}
                    placeholder={getBestUnit(p, matrix, category) ?? getDefaultUnit(p)}
                    paramName={p}
                    matrix={matrix}
                  />
                </td>

                {/* RL — sample-specific reporting limit; legacy rdl values remain readable */}
                <td onFocusCapture={()=>selectCell(p,'reportingLimit')}
                  style={columnCellStyle(p,'reportingLimit',{padding:'3px 4px',background:'#f4f6fb'})}>
                  <input
                    value={r.reportingLimit !== undefined
                      ? r.reportingLimit
                      : (r.rdl !== undefined ? r.rdl : getBestReportingLimit(p))}
                    onChange={e=>setResult(p,'reportingLimit',e.target.value)}
                    placeholder="RL"
                    style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:3,
                      padding:'3px 6px',fontSize:11,textAlign:'right',outline:'none'}}/>
                </td>

                {/* Method (ASTM/EPA/etc.) — combo dropdown with memory */}
                <td onFocusCapture={()=>selectCell(p,'method')}
                  style={columnCellStyle(p,'method',{padding:'2px 3px'})}>
                  <ComboCell
                    value={r.method !== undefined ? r.method : getBestMethod(p, getParamMeta(p).method, matrix)}
                    onChange={v=>setResult(p,'method',v)}
                    options={getAllMethods()}
                    placeholder={getBestMethod(p, getParamMeta(p).method, matrix)||'e.g. D-93'}
                    mono
                    kind="method"
                    paramName={p}
                    matrix={matrix}
                  />
                </td>
                {/* Dilution Factor — stored per analyte; reported results remain final values */}
                <td onFocusCapture={()=>selectCell(p,'dilutionFactor')}
                  style={columnCellStyle(p,'dilutionFactor',{padding:'3px 4px',background:'#eef7fa'})}>
                  <input
                    value={r.dilutionFactor !== undefined ? r.dilutionFactor : ''}
                    onChange={e=>setResult(p,'dilutionFactor',e.target.value)}
                    placeholder="DF"
                    style={{width:'100%',border:'1px solid #b9ced8',borderRadius:3,
                      padding:'3px 5px',fontSize:11,textAlign:'right',outline:'none'}}/>
                </td>
                {/* D975 specification or other method-specific acceptance limits */}
                <td onFocusCapture={()=>selectCell(p,'spec')}
                  style={columnCellStyle(p,'spec',{padding:'2px 3px'})}>
                  <input
                    value={r.spec !== undefined ? r.spec : getBestSpec(p, matrix)}
                    onChange={e=>setResult(p,'spec',e.target.value)}
                    placeholder="Acceptance limit"
                    style={{width:'100%',border:'1px solid #c8d0e8',borderRadius:3,
                      padding:'2px 4px',fontSize:10,outline:'none',
                      background:'#fafafa'}}/>
                </td>
                {/* Date/Time Tested — final column */}
                <td onFocusCapture={()=>selectCell(p,'dateTime')}
                  style={columnCellStyle(p,'dateTime',{padding:'3px 4px'})}>
                  <div style={{display:'flex',gap:2}}>
                    <input type="date"
                      value={r.dateTested||todayStr()}
                      onChange={e=>setResult(p,'dateTested',e.target.value)}
                      style={{flex:1,minWidth:0,border:'1px solid #cdd3e4',borderRadius:3,
                        padding:'2px 3px',fontSize:10,outline:'none',color:'#1e3060'}}/>
                    <input type="time"
                      value={r.timeTested||''}
                      onChange={e=>setResult(p,'timeTested',e.target.value)}
                      title="Time analyzed"
                      style={{width:62,border:'1px solid #cdd3e4',borderRadius:3,
                        padding:'2px 2px',fontSize:10,outline:'none',color:'#1e3060'}}/>
                  </div>
                </td>
              </tr>
            );
          })}
          {showQAQC && params.map((p, i) => {
            const r = results[p] || { value:'' };
            const colSpan = 9 + (showWet?1:0) + (showWetCol?1:0);
            const mdlMode = r.mdlMode || 'std';
            // % Recovery = ((spiked sample result − original result) / spike amount) × 100
            // Auto-calculated whenever spike amount + spiked result are both entered;
            // otherwise left as whatever was manually typed (some labs record it
            // differently, e.g. from an external calc), never silently overwritten
            // if the person already typed something in and the inputs to
            // auto-calculate aren't both present.
            const spikeNum = parseFloat(r.spike), spikeResNum = parseFloat(r.spikeResult), origNum = parseFloat(r.value);
            const autoRecovery = (!isNaN(spikeNum) && spikeNum !== 0 && !isNaN(spikeResNum) && !isNaN(origNum))
              ? round4(((spikeResNum - origNum) / spikeNum) * 100) : null;
            const recoveryDisplay = autoRecovery !== null ? autoRecovery : (r.percentRecovery || '');

            return (
              <tr key={`qaqc-${p}`} style={{background:'#faf5ff',borderBottom:'2px solid #e0d0f0'}}>
                <td colSpan={colSpan} style={{padding:'6px 8px'}}>
                  <div style={{display:'flex',flexWrap:'wrap',gap:14,alignItems:'flex-end'}}>
                    <div style={{fontSize:9,color:'#9c27b0',fontWeight:700,minWidth:70}}>
                      🔬 QA/QC<br/><span style={{fontWeight:400,color:'#888'}}>{p}</span>
                    </div>

                    {/* MDL: Std / Alt toggle + values */}
                    <div>
                      <div style={{fontSize:9,color:'#666',marginBottom:2}}>MDL</div>
                      <div style={{display:'flex',gap:3,alignItems:'center'}}>
                        <button type="button" onClick={()=>setResult(p,'mdlMode','std')}
                          style={{fontSize:9,padding:'2px 6px',borderRadius:3,cursor:'pointer',
                            border:'1px solid #9c27b0',background:mdlMode==='std'?'#9c27b0':'white',
                            color:mdlMode==='std'?'white':'#9c27b0'}}>Std</button>
                        <input value={r.stdMdl||''} onChange={e=>setResult(p,'stdMdl',e.target.value)}
                          placeholder="Std MDL" style={{width:70,border:'1px solid #cdd3e4',borderRadius:3,
                            padding:'2px 5px',fontSize:10,outline:'none',
                            background:mdlMode==='std'?'#f9f0ff':'white'}}/>
                        <button type="button" onClick={()=>setResult(p,'mdlMode','alt')}
                          style={{fontSize:9,padding:'2px 6px',borderRadius:3,cursor:'pointer',
                            border:'1px solid #9c27b0',background:mdlMode==='alt'?'#9c27b0':'white',
                            color:mdlMode==='alt'?'white':'#9c27b0'}}>Alt</button>
                        <input value={r.altMdl||''} onChange={e=>setResult(p,'altMdl',e.target.value)}
                          placeholder="Alt MDL" style={{width:70,border:'1px solid #cdd3e4',borderRadius:3,
                            padding:'2px 5px',fontSize:10,outline:'none',
                            background:mdlMode==='alt'?'#f9f0ff':'white'}}/>
                      </div>
                    </div>

                    {/* Override Result */}
                    <div>
                      <div style={{fontSize:9,color:'#666',marginBottom:2}}>Override Result</div>
                      <input value={r.overrideValue||''} onChange={e=>setResult(p,'overrideValue',e.target.value)}
                        placeholder="(uses Result if blank)"
                        style={{width:120,border:'1px solid #cdd3e4',borderRadius:3,
                          padding:'2px 5px',fontSize:10,outline:'none',
                          background: r.overrideValue ? '#fff8e8' : 'white'}}/>
                    </div>

                    {/* Spike */}
                    <div>
                      <div style={{fontSize:9,color:'#666',marginBottom:2}}>Spike / Result</div>
                      <div style={{display:'flex',gap:3}}>
                        <input value={r.spike||''} onChange={e=>setResult(p,'spike',e.target.value)}
                          placeholder="Spike" style={{width:60,border:'1px solid #cdd3e4',borderRadius:3,
                            padding:'2px 5px',fontSize:10,outline:'none'}}/>
                        <input value={r.spikeResult||''} onChange={e=>setResult(p,'spikeResult',e.target.value)}
                          placeholder="Result" style={{width:60,border:'1px solid #cdd3e4',borderRadius:3,
                            padding:'2px 5px',fontSize:10,outline:'none'}}/>
                      </div>
                    </div>

                    {/* Duplicate Spike */}
                    <div>
                      <div style={{fontSize:9,color:'#666',marginBottom:2}}>Dup. Spike / Result</div>
                      <div style={{display:'flex',gap:3}}>
                        <input value={r.dupSpike||''} onChange={e=>setResult(p,'dupSpike',e.target.value)}
                          placeholder="Dup Spike" style={{width:60,border:'1px solid #cdd3e4',borderRadius:3,
                            padding:'2px 5px',fontSize:10,outline:'none'}}/>
                        <input value={r.dupSpikeResult||''} onChange={e=>setResult(p,'dupSpikeResult',e.target.value)}
                          placeholder="Result" style={{width:60,border:'1px solid #cdd3e4',borderRadius:3,
                            padding:'2px 5px',fontSize:10,outline:'none'}}/>
                      </div>
                    </div>

                    {/* % Recovery */}
                    <div>
                      <div style={{fontSize:9,color:'#666',marginBottom:2}}>
                        % Recovery {autoRecovery !== null && <span style={{color:'#1a6b42'}}>(auto)</span>}
                      </div>
                      <input value={recoveryDisplay}
                        onChange={e=>setResult(p,'percentRecovery',e.target.value)}
                        readOnly={autoRecovery !== null}
                        style={{width:70,border:'1px solid #cdd3e4',borderRadius:3,
                          padding:'2px 5px',fontSize:10,outline:'none',
                          background: autoRecovery !== null ? '#f0fff4' : 'white',
                          color: autoRecovery !== null ? '#1a6b42' : '#333', fontWeight:600}}/>
                    </div>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function EnterResults({ currentSample, onSelectSample, toast, session, onResultsSaved }) {
  const initialMoistureBasis = getSavedMoistureBasis(currentSample);
  const [refreshTick,  setRefreshTick]  = useState(0);
  const [showCompleted, setShowCompleted] = useState(false);
  const samples = useMemo(()=>{
    const all = db.getSamples();
    return showCompleted ? all : all.filter(s=>!s.completed);
  },[refreshTick, showCompleted]);
  const [activeSample, setActiveSample] = useState(currentSample);
  // Seed from the selected sample immediately. The old empty initializer,
  // combined with an app remount, could display a blank form for an already
  // populated sample before loadSample ever ran.
  const [results,      setResults]      = useState(() => currentSample?.results ? {...currentSample.results} : {});
  const [reportComment,setReportComment]= useState(() => loadSampleReportComment(currentSample?.id));
  const [search,       setSearch]       = useState('');
  const [recalcParams, setRecalcParams] = useState(() => new Set(initialMoistureBasis.recalcParams));
  const [showBasis,    setShowBasis]    = useState(initialMoistureBasis.showBasis);
  const [showCalc,     setShowCalc]     = useState(false);
  const [reportMode,   setReportMode]   = useState(initialMoistureBasis.reportMode); // 'dry'|'wet'|'both'
  const [showWetCol,   setShowWetCol]   = useState(initialMoistureBasis.enabled); // toggle wet basis column
  const [showQAQC,     setShowQAQC]     = useState(false); // toggle RL/MDL/Override/Spike/Recovery sub-rows
  const [includeSpecificationInReport, setIncludeSpecificationInReport] = useState(
    currentSample?.includeSpecificationInReport === true
  );
  const [moistureInput,setMoistureInput]= useState(initialMoistureBasis.moisturePercent); // quick moisture % input for wet calc
  const [isDirty,       setIsDirty]       = useState(false);
  const [isSaving,      setIsSaving]      = useState(false);
  const [showWorkOrderGrid, setShowWorkOrderGrid] = useState(false);
  const touchedFieldsRef = React.useRef(new Set());
  const initialLoadRef = React.useRef(false);
  const editLockOwner = 'enter-results';
  const draftKey = sampleId => `sl_result_recovery_draft_${sampleId}`;

  const clearRecoveryDraft = sampleId => {
    try { window.localStorage.removeItem(draftKey(sampleId)); } catch {}
  };

  const readRecoveryDraft = sampleId => {
    try { return JSON.parse(window.localStorage.getItem(draftKey(sampleId)) || 'null'); }
    catch { return null; }
  };

  const markDirty = () => {
    beginUiEdit(editLockOwner);
    setIsDirty(true);
  };

  useEffect(() => () => endUiEdit(editLockOwner), []);

  // Keep an immediate local recovery copy until PostgreSQL confirms the save.
  // This is intentionally independent of the normal server write queue.
  useEffect(() => {
    if (!activeSample?.id || !isDirty) return;
    try {
      window.localStorage.setItem(draftKey(activeSample.id), JSON.stringify({
        results,
        touchedFields:[...touchedFieldsRef.current],
        moistureBasis:{
          enabled:showWetCol,
          moisturePercent:moistureInput,
          recalcParams:[...recalcParams],
          reportMode,
          showBasis,
        },
        includeSpecificationInReport,
        updatedAt:new Date().toISOString(),
      }));
    } catch {}
  }, [results, isDirty, activeSample?.id, showWetCol, moistureInput, recalcParams, reportMode, showBasis, includeSpecificationInReport]);

  useEffect(()=>{
    if (currentSample && currentSample.id !== activeSample?.id) switchSample(currentSample);
  },[currentSample]);

  useEffect(() => {
    if (!initialLoadRef.current && currentSample) {
      initialLoadRef.current = true;
      loadSample(currentSample);
    }
  }, []);

  const loadSample = (s) => {
    setActiveSample(s);
    const recovery = readRecoveryDraft(s.id);
    const savedMoistureBasis = recovery?.moistureBasis || getSavedMoistureBasis(s);
    const recoveryHasChanges = recovery?.results && Array.isArray(recovery.touchedFields) && (
      recovery.touchedFields.length > 0 ||
      recovery.includeSpecificationInReport !== undefined
    );
    if (recoveryHasChanges) {
      touchedFieldsRef.current = new Set(recovery.touchedFields);
      setResults({ ...(s.results || {}), ...recovery.results });
      setIsDirty(true);
      beginUiEdit(editLockOwner);
      setTimeout(() => toast('warn', `Recovered unsaved result entries for Sample ${s.id}.`), 0);
    } else {
      touchedFieldsRef.current = new Set();
      setResults(s.results ? {...s.results} : {});
      setIsDirty(false);
      endUiEdit(editLockOwner);
    }
    setReportComment(loadSampleReportComment(s.id));
    setRecalcParams(new Set(savedMoistureBasis.recalcParams));
    setShowWetCol(savedMoistureBasis.enabled);
    setMoistureInput(savedMoistureBasis.moisturePercent);
    setReportMode(savedMoistureBasis.reportMode);
    setShowBasis(savedMoistureBasis.showBasis);
    setIncludeSpecificationInReport(
      recovery?.includeSpecificationInReport !== undefined
        ? recovery.includeSpecificationInReport === true
        : s.includeSpecificationInReport === true
    );
    onSelectSample(s);
  };

  // Never discard a dirty sample merely because the technician clicked the
  // next sample. Commit and verify it first; stay on the current sample if the
  // server save fails so the entries and moisture selections remain visible.
  const switchSample = async s => {
    if (!s || String(s.id) === String(activeSample?.id) || isSaving) return;
    if (isDirty) {
      toast('info', `Saving Sample ${activeSample.id} before switching…`);
      const saved = await doSaveResults();
      if (!saved) return;
    }
    const fresh = db.getSamples().find(sample => String(sample.id) === String(s.id)) || s;
    loadSample(fresh);
  };

  const refresh = ()=>setRefreshTick(t=>t+1);

  const setResult = (param, field, val) => {
    touchedFieldsRef.current.add(`${param}\u0000${field}`);
    markDirty();
    setResults(prev=>({
      ...prev,
      [param]: {
        ...(prev[param]||{value:'',unit:getDefaultUnit(param),qual:'',dateTested:todayStr()}),
        [field]: val
      }
    }));
  };

  const setAllDates = (date) => {
    params.forEach(p => touchedFieldsRef.current.add(`${p}\u0000dateTested`));
    markDirty();
    setResults(prev=>{
      const u={...prev};
      params.forEach(p=>{
        u[p]={...(u[p]||{value:'',unit:getDefaultUnit(p),qual:''}),dateTested:date};
      });
      return u;
    });
  };

  const toggleRecalc = (p, checked) => {
    markDirty();
    setRecalcParams(prev=>{
      const n=new Set(prev); checked?n.add(p):n.delete(p); return n;
    });
  };

  // Apply calculated values from the moisture calculator into results
  const applyCalcToResults = (calcResults) => {
    markDirty();
    setResults(prev => {
      const updated = { ...prev };
      Object.entries(calcResults).forEach(([label, data]) => {
        // Try to match by label against params
        const matchedParam = params.find(p =>
          p.toLowerCase().includes(label.toLowerCase().split('(')[0].trim().toLowerCase()) ||
          label.toLowerCase().includes(p.toLowerCase().split(',')[0].trim().toLowerCase())
        );
        const key = matchedParam || label;
        touchedFieldsRef.current.add(`${key}\u0000value`);
        touchedFieldsRef.current.add(`${key}\u0000dryBasis`);
        updated[key] = {
          ...(updated[key] || { unit: data.unit || '%', qual: '', dateTested: todayStr() }),
          value: data.ar !== undefined ? String(data.ar) : (data.ad !== undefined ? String(data.ad) : ''),
          dryBasis: data.db !== undefined ? String(data.db) : '',
        };
      });
      return updated;
    });
    toast('success', 'Calculated values applied to results');
    setShowCalc(false);
  };

  const saveResults = () => {
    if (!activeSample){toast('error','No sample selected');return;}
    return doSaveResults();
  };

  const doSaveResults = async () => {
    if (!activeSample){toast('error','No sample selected');return;}
    if (isSaving) return;
    setIsSaving(true);
    // Start with the newest cached server record and apply only fields that
    // the user actually edited. Never turn an untouched saved value into a
    // blank just because the current form was incomplete or remounted.
    const latestSample = db.getSamples().find(s => String(s.id) === String(activeSample.id)) || activeSample;
    const finalResults = JSON.parse(JSON.stringify(latestSample.results || {}));
    params.forEach(p => {
      const touched = [...touchedFieldsRef.current]
        .filter(key => key.startsWith(`${p}\u0000`))
        .map(key => key.slice(p.length + 1));
      if (!touched.length) return;
      const local = results[p] || {};
      const existing = finalResults[p] || {};
      const merged = { ...existing };
      touched.forEach(field => { merged[field] = local[field]; });
      merged.unit = local.unit !== undefined
        ? local.unit
        : (existing.unit !== undefined
          ? existing.unit
          : (getBestUnit(p, currentMatrix, activeSample.testCategory) ?? getDefaultUnit(p)));
      merged.method = local.method !== undefined ? local.method
        : (existing.method !== undefined ? existing.method : getBestMethod(p, getParamMeta(p).method, currentMatrix));
      merged.reportingLimit = local.reportingLimit !== undefined ? local.reportingLimit
        : (existing.reportingLimit !== undefined ? existing.reportingLimit
          : (existing.rdl !== undefined ? existing.rdl : getBestReportingLimit(p)));
      merged.spec = local.spec !== undefined ? local.spec
        : (existing.spec !== undefined ? existing.spec : getBestSpec(p, currentMatrix));
      merged.dilutionFactor = local.dilutionFactor !== undefined ? local.dilutionFactor
        : (existing.dilutionFactor !== undefined ? existing.dilutionFactor : '');
      merged.dateTested = local.dateTested || existing.dateTested || todayStr();
      merged.value = local.value !== undefined ? local.value : (existing.value ?? '');
      finalResults[p] = merged;
    });
    const m  = parseFloat(moistureVal);
    const qm = parseFloat(moistureInput);
    const wetColActive = showWetCol && !isNaN(qm) && qm >= 0 && qm < 100;

    const touchedParams = new Set([...touchedFieldsRef.current].map(key => key.split('\u0000')[0]));
    const previousBasis = getSavedMoistureBasis(latestSample);
    const basisAffectedParams = new Set([
      ...previousBasis.recalcParams,
      ...recalcParams,
      ...Object.keys(finalResults).filter(p => finalResults[p]?.moisturePercent != null),
    ]);
    const paramsToFinalize = new Set([...touchedParams, ...basisAffectedParams]);
    Object.keys(finalResults).forEach(p => {
      if (!paramsToFinalize.has(p)) return;
      const r = finalResults[p];
      if (!r) return;

      // A field the tech explicitly cleared (empty string) is a real,
      // intentional choice — it should stay empty in the saved result, not
      // get silently replaced with the fallback default. Fallback only
      // applies when the field was never touched at all (undefined).
      const method = r.method !== undefined ? r.method : getBestMethod(p, getParamMeta(p).method, currentMatrix);
      // Preparation methods are valid only when the tech explicitly applied
      // them to checked tests. Never infer an assignment from a legacy value:
      // older builds silently copied defaults into results, and those values
      // are indistinguishable from real selections without this marker.
      const extractionMethodAssigned = r.extractionMethodAssigned === true;
      const digestionMethodAssigned = r.digestionMethodAssigned === true;
      const extractionMethod = extractionMethodAssigned ? r.extractionMethod : '';
      const digestionMethod = digestionMethodAssigned ? r.digestionMethod : '';
      const spec   = r.spec   !== undefined ? r.spec   : '';
      const isMoist = isMoistureParam(p);

      if (wetColActive && recalcParams.has(p) && r.value && !isMoist) {
        // Wet basis column enabled — calculate only the parameters selected above the table
        const qWet = calcWetBasis(r.value, qm);
        finalResults[p] = {
          ...r,
          dryBasis:   r.value,
          wetBasis:   qWet !== null ? storeCalculatedValue(qWet) : '',
          reportMode: qWet !== null ? 'both' : 'dry',
          moisturePercent: qm, // BUGFIX: this was never persisted, so the report
                               // (and reopening this sample) had no way to know
                               // what moisture % was actually used to calculate it
          method, extractionMethod, digestionMethod,
          extractionMethodAssigned, digestionMethodAssigned, spec,
        };
      } else if (wetColActive && isMoist) {
        // Moisture param itself — save as plain, no wet recalc
        finalResults[p] = {
          ...r, dryBasis: r.value, wetBasis: '', reportMode: 'dry',
          method, extractionMethod, digestionMethod,
          extractionMethodAssigned, digestionMethodAssigned, spec
        };
      } else if (isSolidSample && recalcParams.has(p) && !isNaN(m) && m < 100) {
        // Solid sample recalc via moisture param
        const wet_v = calcWetBasis(r.value, m);
        finalResults[p] = {
          ...r,
          dryBasis:   r.value || '',
          wetBasis:   wet_v !== null ? storeCalculatedValue(wet_v) : '',
          reportMode: wet_v !== null ? reportMode : 'dry',
          moisturePercent: m, // same fix, for the dedicated-Moisture-parameter path
          method, extractionMethod, digestionMethod,
          extractionMethodAssigned, digestionMethodAssigned, spec,
        };
      } else {
        // Plain result
        finalResults[p] = {
          ...r, dryBasis: '', wetBasis: '', reportMode: 'none', moisturePercent: null,
          method, extractionMethod, digestionMethod,
          extractionMethodAssigned, digestionMethodAssigned, spec
        };
      }
    });
    try {
      // Do not announce success until PostgreSQL has committed the record.
      // The previous fire-and-forget save let a refresh reload the last
      // server value (for example 0.17) while this PATCH was still pending.
      const saved = await db.updateSample(activeSample.id, {
        results: finalResults,
        recalcParams: [...recalcParams],
        moistureBasis: {
          enabled:showWetCol,
          moisturePercent:moistureInput,
          recalcParams:[...recalcParams],
          reportMode,
          showBasis,
        },
        includeSpecificationInReport,
      });
      let confirmedSample = saved?.record;
      if (!confirmedSample) {
        throw new Error('The server did not return a saved sample record');
      }
      const serverResults = confirmedSample.results || {};
      const mismatched = [...touchedFieldsRef.current].filter(key => {
        const splitAt = key.indexOf('\u0000');
        const param = key.slice(0, splitAt);
        const field = key.slice(splitAt + 1);
        return String(serverResults[param]?.[field] ?? '') !== String(finalResults[param]?.[field] ?? '');
      });
      if (mismatched.length) {
        throw new Error(`Server verification failed for: ${mismatched.join(', ')}`);
      }
      const confirmedBasis = getSavedMoistureBasis(confirmedSample);
      const expectedRecalc = [...recalcParams].sort().join('\u0000');
      const confirmedRecalc = [...confirmedBasis.recalcParams].sort().join('\u0000');
      if (confirmedBasis.enabled !== showWetCol ||
          String(confirmedBasis.moisturePercent) !== String(moistureInput) ||
          confirmedBasis.reportMode !== reportMode ||
          confirmedBasis.showBasis !== showBasis ||
          confirmedRecalc !== expectedRecalc) {
        throw new Error('Server verification failed for moisture/basis settings');
      }
      if ((confirmedSample.includeSpecificationInReport === true) !== includeSpecificationInReport) {
        throw new Error('Server verification failed for specification report setting');
      }
      const basisMismatched = [...basisAffectedParams].filter(p => {
        const expected = finalResults[p] || {};
        const actual = serverResults[p] || {};
        return ['dryBasis','wetBasis','reportMode','moisturePercent'].some(field =>
          String(actual[field] ?? '') !== String(expected[field] ?? '')
        );
      });
      if (basisMismatched.length) {
        throw new Error(`Server verification failed for moisture result(s): ${basisMismatched.join(', ')}`);
      }
      const confirmedResults = confirmedSample.results || {};
      setActiveSample(confirmedSample);
      setResults({...confirmedResults});
      onSelectSample(confirmedSample);
      touchedFieldsRef.current = new Set();
      clearRecoveryDraft(activeSample.id);
      setIsDirty(false);
      endUiEdit(editLockOwner);
      // The sample save is already confirmed. Wait for the unit/specification
      // defaults as well so the next sample cannot reload an older default.
      Promise.resolve(saveSampleReportComment(activeSample.id, reportComment)).catch(error =>
        console.error('[LIMS] Report comment preference failed:', error));
      let defaultsSaved = true;
      try {
        await saveResultFieldDefaults(params, finalResults, currentMatrix, activeSample.testCategory);
      } catch (error) {
        defaultsSaved = false;
        console.error('[LIMS] Result defaults preference failed:', error);
        toast('warn', `Results were saved for Sample ${activeSample.id}, but the last-used Unit/Specification defaults were not saved: ${error.message}`);
      }
      if (defaultsSaved) toast('success',`Results and last-used defaults confirmed saved for Sample ${activeSample.id}`);
      onResultsSaved && onResultsSaved();
      return confirmedSample;
    } catch (error) {
      // Keep the typed values on screen so the technician can retry or copy
      // them. Conflict handling separately loads the newer server record.
      markDirty();
      toast('error', `Results were not saved: ${error.message}`);
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const markComplete = async () => {
    if (!activeSample){toast('error','No sample selected');return;}
    const savedSample = await doSaveResults();
    if (!savedSample) return;
    try {
      const completed = await db.updateSample(activeSample.id,{
        completed:true,
        dateCompleted:new Date().toISOString().split('T')[0]
      });
      if (completed?.taskSync?.status === 'failed') {
        toast('warn',`Sample ${activeSample.id} was completed, but employee tasks did not sync. Ask an administrator to check the task sheet.`);
      } else {
        toast('success',`Sample ${activeSample.id} marked complete; ${completed?.taskSync?.updated || 0} employee task(s) synced`);
      }
      refresh();
      const updated=completed?.record || db.getSamples().find(s=>s.id===activeSample.id);
      setActiveSample(updated);
      onSelectSample(updated);
    } catch (error) {
      toast('error', `Results were saved, but completion failed: ${error.message}`);
    }
  };

  const params = activeSample?.params?.length>0
    ? activeSample.params
    : activeSample
      ? ['Moisture, %','Ash,%','BTU/Lb','Sulfur, %','Fixed Carbon, %',
         'Volatile Matter, %','Carbon,%','Hydrogen,%','Nitrogen,%']
      : [];
  const currentMatrix = activeSample?.solidLiq === 'S' ? 'S' : activeSample?.solidLiq === 'T' ? 'T' : 'L';

  // Live moisture and ash values from current results
  const moistureVal = useMemo(()=>{
    const mp = params.find(p=>isMoistureParam(p));
    return mp ? (results[mp]?.value||'') : '';
  },[results,params]);

  const ashVal = useMemo(()=>{
    const ap = params.find(p=>isAshParam(p));
    return ap ? (results[ap]?.value||'') : '';
  },[results,params]);

  const hasMoistureParam = params.some(p=>isMoistureParam(p));
  // Dry/Wet basis only applies to solid samples
  const SOLID_FORMS = ['Solid','Sludge','Soil','Coal','Biomass'];
  const isSolidSample = SOLID_FORMS.some(f =>
    (activeSample?.sampleForm||'').toLowerCase().includes(f.toLowerCase()) ||
    (activeSample?.testType||'').toLowerCase().includes('coal') ||
    (activeSample?.testType||'').toLowerCase().includes('biomass') ||
    (activeSample?.testCategory||'').toLowerCase().includes('solid')
  );
  // Show moisture/basis controls only for solid samples that have moisture param
  const showMoistureBasis = hasMoistureParam && isSolidSample;

  const filtered = samples.filter(s=>{
    if(!search) return true;
    const q=search.toLowerCase();
    return String(s.id).includes(q)||s.workOrder?.toLowerCase().includes(q)||s.source?.toLowerCase().includes(q);
  });

  return (
    <>
      <div className="toolbar">
        <input className="search-input"
          placeholder="Sample ID, work order, source..."
          value={search} onChange={e=>setSearch(e.target.value)} style={{width:240}}/>
        <div className="tb-sep"/>
        <button className="btn btn-primary" onClick={saveResults}
          disabled={!activeSample || isSaving}>
          {isSaving ? '⏳ Saving…' : `💾 Save Results${isDirty ? ' *' : ''}`}
        </button>
        <button className="btn" onClick={()=>setShowWorkOrderGrid(true)}
          disabled={!activeSample?.workOrder || isSaving || isDirty}
          style={{borderColor:'#2e7d32',color:'#246b28',fontWeight:700}}
          title={isDirty
            ? 'Save or discard the current sample changes before opening the Work Order Grid'
            : 'Paste instrument results into all samples in this Work Order'}>
          📊 Work Order Grid
        </button>
        <button className="btn btn-success" onClick={markComplete} disabled={!activeSample}>
          ✅ Mark Complete
        </button>
        {activeSample?.completed && (
          <button className="btn" onClick={()=>{
            if (!activeSample) return;
            db.updateSample(activeSample.id,{completed:false,dateCompleted:''});
            toast('info',`Sample ${activeSample.id} marked incomplete`);
            refresh();
            const updated=db.getSamples().find(s=>s.id===activeSample.id);
            setActiveSample(updated);
          }}
          style={{borderColor:'#e09900',color:'#e09900'}}>
            ↩ Mark Incomplete
          </button>
        )}
        <div className="tb-sep"/>
        {showMoistureBasis && (
          <label style={{display:'flex',alignItems:'center',gap:5,
            fontSize:11,cursor:'pointer',fontWeight:600,color:'#1a7dc4'}}>
            <input type="checkbox" checked={showBasis}
              onChange={e=>{ markDirty(); setShowBasis(e.target.checked); }}
              style={{accentColor:'#1a7dc4'}}/>
            💧 Dry/As Received Basis
          </label>
        )}
        {/* Wet basis toggle — works for any sample type */}
        <label style={{display:'flex',alignItems:'center',gap:5,
          fontSize:11,cursor:'pointer',fontWeight:600,
          color:showWetCol?'#1a7dc4':'#888'}}>
          <input type="checkbox" checked={showWetCol}
            onChange={e=>{ markDirty(); setShowWetCol(e.target.checked); }}
            style={{accentColor:'#1a7dc4'}}/>
          💧 Add As Received column
        </label>
        <label style={{display:'flex',alignItems:'center',gap:5,
          fontSize:11,cursor:'pointer',fontWeight:600,
          color:includeSpecificationInReport?'#2e7d32':'#888'}}
          title="Include Specification / Acceptance Limits on this sample's report">
          <input type="checkbox" checked={includeSpecificationInReport}
            onChange={e=>{ markDirty(); setIncludeSpecificationInReport(e.target.checked); }}
            style={{accentColor:'#2e7d32'}}/>
          ✅ Include Specification in report
        </label>
        <label style={{display:'flex',alignItems:'center',gap:5,
          fontSize:11,cursor:'pointer',fontWeight:600,
          color:showQAQC?'#9c27b0':'#888'}}>
          <input type="checkbox" checked={showQAQC}
            onChange={e=>setShowQAQC(e.target.checked)}
            style={{accentColor:'#9c27b0'}}/>
          🔬 Show QA/QC (RL, MDL, Override, Spike/Recovery)
        </label>
        {showWetCol && (
          <div style={{display:'flex',alignItems:'center',gap:5,
            background:'#e8f4ff',border:'1px solid #aac8ee',borderRadius:4,
            padding:'3px 8px'}}>
            <span style={{fontSize:11,fontWeight:600,color:'#1e3060'}}>
              Moisture %:
            </span>
            <input
              value={moistureInput}
              onChange={e=>{ markDirty(); setMoistureInput(e.target.value); }}
              placeholder="e.g. 8.5"
              style={{width:70,border:'1px solid #aac8ee',borderRadius:3,
                padding:'3px 6px',fontSize:11,outline:'none',textAlign:'right'}}/>
            <span style={{fontSize:10,color:'#888'}}>
              AR = DB × (1−M%/100)
            </span>
          </div>
        )}
        {showMoistureBasis && (
          <div style={{display:'flex',alignItems:'center',gap:4,
            background:'#f0f2f8',borderRadius:5,padding:2}}>
            <span style={{fontSize:10,color:'#888',padding:'0 6px',fontWeight:600}}>Report:</span>
            {[['dry','Dry Basis'],['wet','As Received (AR)'],['both','Both']].map(([v,l])=>(
              <button key={v} onClick={()=>{ markDirty(); setReportMode(v); }}
                style={{padding:'4px 10px',borderRadius:4,border:'none',fontSize:11,
                  cursor:'pointer',fontWeight:600,
                  background:reportMode===v?'#2a5080':'transparent',
                  color:reportMode===v?'white':'#555'}}>
                {l}
              </button>
            ))}
          </div>
        )}
        {showMoistureBasis && (
          <button className="btn"
            style={{background:showCalc?'#1a7dc4':'#e8f4ff',
              borderColor:'#1a7dc4',color:showCalc?'white':'#1a7dc4',fontWeight:600}}
            onClick={()=>setShowCalc(v=>!v)}>
            🧮 {showCalc?'Hide':'Show'} Moisture Calculator
          </button>
        )}
        <div className="tb-sep"/>
        <span className="tb-label">Sample:</span>
        <span className="tb-value mono">{activeSample?.id||'Select →'}</span>
        {activeSample && (
          <span className="text-muted text-sm">
            {activeSample.source} | {activeSample.testType}
          </span>
        )}
      </div>

      <div className="content">
        <div style={{display:'grid',gridTemplateColumns:'260px 1fr',gap:12,height:'100%'}}>

          {/* Left: sample list */}
          <div className="panel" style={{height:'calc(100vh - 140px)'}}>
            <div className="panel-header" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <span>📋 {showCompleted?'All':'Pending'} ({filtered.length})</span>
              <div style={{display:'flex',gap:4}}>
                <button onClick={()=>setShowCompleted(v=>!v)}
                  style={{background:'none',border:'1px solid rgba(255,255,255,0.3)',
                    borderRadius:3,color:'white',fontSize:9,padding:'1px 5px',cursor:'pointer'}}>
                  {showCompleted?'Pending only':'Show all'}
                </button>
                <button onClick={refresh}
                  style={{background:'none',border:'1px solid rgba(255,255,255,0.3)',
                    borderRadius:3,color:'white',fontSize:11,padding:'1px 5px',cursor:'pointer'}}
                  title="Refresh list">↺</button>
              </div>
            </div>
            <div style={{overflowY:'auto',height:'calc(100% - 36px)'}}>
              <table className="data-table">
                <thead><tr><th>Sample</th><th>Type</th></tr></thead>
                <tbody>
                  {filtered.map(s=>(
                    <tr key={s.id}
                      className={`clickable ${activeSample?.id===s.id?'selected':''}`}
                      style={{opacity:s.completed?0.6:1}}
                      onClick={()=>switchSample(s)}>
                      <td>
                        <strong className="mono">{s.id}</strong>
                        {s.completed && <span style={{marginLeft:4,fontSize:9,
                          color:'#1a6b42',fontWeight:700}}>✓</span>}
                        <div className="text-muted text-xs">{s.workOrder}</div>
                        <div className="text-xs" style={{overflow:'hidden',
                          textOverflow:'ellipsis',maxWidth:100}}>{s.source}</div>
                      </td>
                      <td>
                        <span className={`badge badge-${s.testType?.toLowerCase()}`}>
                          {s.testType}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {filtered.length===0 && (
                    <tr><td colSpan={2} className="text-center text-muted"
                      style={{padding:16}}>No pending samples</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right: results entry */}
          <div className="panel" style={{height:'calc(100vh - 140px)'}}>
            <div className="panel-header">
              🔬 Results Entry
              {activeSample && (
                <span style={{marginLeft:10,fontSize:11,fontWeight:400,color:'#aac4e0'}}>
                  {activeSample.source} — {activeSample.received} — {activeSample.testCategory}
                </span>
              )}
            </div>
            <div style={{padding:8,overflowY:'auto',overflowX:'visible',height:'calc(100% - 36px)'}}>
              {!activeSample ? (
                <div className="notice notice-info" style={{marginTop:20}}>
                  ← Select a sample from the list to enter results
                </div>
              ) : (
                <>
                  <div style={{padding:'9px 12px',marginBottom:8,background:'#f7f9fd',
                    border:'1px solid #c7d2e5',borderRadius:5,fontSize:13,
                    lineHeight:1.35,color:'#1f3150'}}>
                    <div style={{display:'flex',alignItems:'baseline',flexWrap:'wrap',
                      columnGap:22,rowGap:4}}>
                      <span><strong>Sample ID:</strong> <span className="mono">{activeSample.id}</span></span>
                      <span><strong>Work Order:</strong> {activeSample.workOrder || '—'}</span>
                      <span><strong>Client/Source:</strong> {activeSample.companyName || activeSample.source || '—'}</span>
                      <span><strong>Date Received:</strong> {activeSample.received || '—'}</span>
                      <span><strong>Test Category:</strong> {activeSample.testCategory || activeSample.testType || '—'}</span>
                    </div>
                    <div style={{display:'flex',alignItems:'baseline',gap:7,marginTop:7,
                      paddingTop:7,borderTop:'1px solid #d6deec'}}>
                      <strong style={{whiteSpace:'nowrap'}}>Sample Description:</strong>
                      <span style={{fontWeight:500}}>{activeSample.desc || activeSample.description || '—'}</span>
                    </div>
                  </div>
                  <ResultsSheetPanel sampleId={activeSample.id} toast={toast}/>
                  {/* Info bar */}
                  <div className="notice notice-info" style={{marginBottom:8}}>
                    {params.length} parameters assigned.
                    {activeSample.params?.length===0 &&
                      ' No setup yet — showing default params. Go to Sample Setup first.'}
                  </div>

                  {/* Notice for non-solid samples */}
                  {hasMoistureParam && !isSolidSample && (
                    <div style={{background:'#fff8e8',border:'1px solid #e0c060',borderRadius:4,
                      padding:'6px 10px',marginBottom:8,fontSize:11,color:'#7a5a00',display:'flex',
                      alignItems:'center',gap:6}}>
                      💧 Dry/Wet basis not applicable for <strong>{activeSample?.sampleForm||'this sample type'}</strong>.
                      Set Sample Form to Solid, Sludge, or Soil to enable basis calculations.
                    </div>
                  )}
                  {/* Set all dates */}
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8,
                    padding:'6px 10px',background:'#f0f4ff',borderRadius:4,
                    border:'1px solid #e0e4ec'}}>
                    <span style={{fontSize:11,fontWeight:600,color:'#1e3060'}}>
                      Set date for all:
                    </span>
                    <input type="date" defaultValue={todayStr()}
                      onChange={e=>setAllDates(e.target.value)}
                      style={{border:'1px solid #cdd3e4',borderRadius:4,
                        padding:'3px 8px',fontSize:11,outline:'none',
                        color:'#1e3060',cursor:'pointer'}}/>
                    <span style={{fontSize:10,color:'#888'}}>
                      or set individually per parameter
                    </span>
                  </div>

                  {/* Moisture Calculator panel */}
                  {showMoistureBasis && showCalc && (
                    <div style={{border:'2px solid #1a7dc4',borderRadius:8,
                      marginBottom:12,overflow:'hidden'}}>
                      <div style={{background:'#1a7dc4',color:'white',
                        padding:'8px 14px',display:'flex',alignItems:'center',
                        justifyContent:'space-between'}}>
                        <span style={{fontWeight:700,fontSize:13}}>
                          🧮 Moisture & Basis Calculator
                        </span>
                        <button onClick={()=>setShowCalc(false)}
                          style={{background:'none',border:'none',color:'rgba(255,255,255,0.7)',
                            cursor:'pointer',fontSize:18,lineHeight:1}}>✕</button>
                      </div>
                      <div style={{padding:14,background:'white'}}>
                        <MoistureCalculator
                          onClose={()=>setShowCalc(false)}
                          onApplyToResults={applyCalcToResults}
                        />
                      </div>
                    </div>
                  )}

                  {/* Moisture basis panel — only for solid samples */}
                  {((showMoistureBasis && showBasis) || showWetCol) && (
                    <MoistureBasisPanel
                      params={params}
                      results={results}
                      recalcParams={recalcParams}
                      onToggleRecalc={toggleRecalc}
                      moistureVal={showWetCol ? moistureInput : moistureVal}
                      ashVal={ashVal}
                    />
                  )}

                  {/* Basis legend */}
                  {showMoistureBasis && showBasis && moistureVal !== '' && (
                    <div style={{display:'flex',gap:12,marginBottom:8,
                      padding:'6px 12px',background:'#f0f6ff',borderRadius:4,
                      border:'1px solid #aac8ee',fontSize:11,flexWrap:'wrap',
                      alignItems:'center'}}>
                      <strong style={{color:'#1e3060'}}>Formulas:</strong>
                      <span style={{display:'flex',alignItems:'center',gap:5}}>
                        <span style={{width:12,height:12,borderRadius:2,background:'#f0f6ff',
                          border:'2px solid #2a5080',display:'inline-block'}}/>
                        <strong>Dry Basis</strong>
                        <span style={{color:'#888'}}>= entered value</span>
                        <span style={{background:'#2a5080',color:'white',fontSize:9,
                          padding:'1px 6px',borderRadius:8,fontWeight:700}}>INPUT</span>
                      </span>
                      <span style={{display:'flex',alignItems:'center',gap:5}}>
                        <span style={{width:12,height:12,borderRadius:2,background:'#e8f4ff',
                          border:'2px solid #1a7dc4',display:'inline-block'}}/>
                        <strong>As Received (AR)</strong>
                        <span style={{color:'#888'}}>= DB × (1 − M%/100)</span>
                        <span style={{background:'#1a7dc4',color:'white',fontSize:9,
                          padding:'1px 6px',borderRadius:8,fontWeight:700}}>
                          M = {moistureVal}%
                        </span>
                      </span>

                    </div>
                  )}

                  {/* Results table */}
                  {params.length>0 ? (
                    <>
                      <ResultsTable
                        sampleId={activeSample.id}
                        matrix={currentMatrix}
                        category={activeSample.testCategory}
                        params={params}
                        results={results}
                        setResult={setResult}
                        recalcParams={recalcParams}
                        moistureVal={moistureVal}
                        showBasis={showMoistureBasis && showBasis}
                        reportMode={reportMode}
                        showWetCol={showWetCol}
                        showQAQC={showQAQC}
                        quickMoisture={moistureInput}
                      />
                      <div style={{marginTop:10,border:'1px solid #c9d2e3',borderRadius:6,
                        background:'#f8faff',overflow:'hidden'}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',
                          padding:'7px 10px',background:'#e9eef8',borderBottom:'1px solid #c9d2e3'}}>
                          <strong style={{fontSize:11,color:'#1e3060'}}>
                            📝 Report Comments
                          </strong>
                          <span style={{fontSize:9,color:'#666'}}>
                            Client-visible · printed below the result table
                          </span>
                        </div>
                        <textarea
                          value={reportComment}
                          onChange={e=>{ markDirty(); setReportComment(e.target.value); }}
                          placeholder={'Enter the complete statement for the issued report.\\nExample: The sample fails the ASTM D975 acceptance limit for water and sediment.'}
                          rows={4}
                          style={{display:'block',width:'100%',border:'none',resize:'vertical',
                            padding:'9px 10px',fontSize:11,lineHeight:1.5,fontFamily:'inherit',
                            outline:'none',background:'white',boxSizing:'border-box'}}/>
                        <div style={{padding:'5px 10px',fontSize:9,color:'#777',
                          borderTop:'1px solid #e1e6ef'}}>
                          Line breaks are preserved. This text is saved when you click Save Results.
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="notice notice-warn">
                      No parameters set up. Go to Sample Setup first.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {showWorkOrderGrid && activeSample && (
        <WorkOrderResultsGrid
          activeSample={activeSample}
          session={session}
          toast={toast}
          onClose={()=>setShowWorkOrderGrid(false)}
          onSaved={()=>{
            refresh();
            const updated = db.getSamples().find(sample =>
              String(sample.id) === String(activeSample.id)
            );
            if (updated) loadSample(updated);
            onResultsSaved && onResultsSaved();
          }}/>
      )}
    </>
  );
}
